/**
 * Upload Question Modal Component
 * 
 * Modal for uploading question sets with file upload
 * 
 * @component
 */

import React, { useState, useRef, useMemo } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { message } from "antd";
import { useTheme } from "../../ThemeContext";
import { TextBox, Dropdown } from "@voicecare.ai/ui-library";
import QuestionService from "../../services/question-service";

/**
 * Helper function to format names (capitalize first letter, replace underscores/hyphens with spaces)
 */
const formatName = (name) => {
  if (!name) return '';
  return name
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * UploadQuestionModal Component
 */
const UploadQuestionModal = ({
  open,
  onClose,
  onSuccess,
  customerDetails,
  onPathChange
}) => {
  const { themeMode, currentTheme } = useTheme();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    questionSetName: '',
    branch: null,
    usecase: null,
    subusecase: null
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({
    questionSetName: '',
    branch: '',
    usecase: '',
    subusecase: '',
    file: ''
  });
  const [touched, setTouched] = useState(false);

  const isDark = themeMode === 'dark';

  /**
   * Extract unique branches from customer details and format for dropdown
   */
  const branchOptions = useMemo(() => {
    if (!customerDetails?.useCases) return [];
    const uniqueBranches = [...new Set(customerDetails.useCases.map(uc => uc.branch))];
    return uniqueBranches.sort().map(branch => ({
      value: branch,
      label: formatName(branch)
    }));
  }, [customerDetails]);

  /**
   * Get usecases for selected branch and format for dropdown
   */
  const usecaseOptions = useMemo(() => {
    if (!customerDetails?.useCases || !formData.branch) return [];
    const branchUseCases = customerDetails.useCases.filter(uc => uc.branch === formData.branch.value);
    const uniqueUseCases = [...new Set(branchUseCases.map(uc => uc.usecase))];
    return uniqueUseCases.sort().map(usecase => ({
      value: usecase,
      label: formatName(usecase)
    }));
  }, [customerDetails, formData.branch]);

  /**
   * Get subusecases for selected branch and usecase and format for dropdown
   */
  const subusecaseOptions = useMemo(() => {
    if (!customerDetails?.useCases || !formData.branch || !formData.usecase) return [];
    const filteredUseCases = customerDetails.useCases.filter(
      uc => uc.branch === formData.branch.value && uc.usecase === formData.usecase.value
    );
    const uniqueSubUseCases = [...new Set(filteredUseCases.flatMap(uc => uc.subUsecases))];
    return uniqueSubUseCases.sort().map(subusecase => ({
      value: subusecase,
      label: formatName(subusecase)
    }));
  }, [customerDetails, formData.branch, formData.usecase]);

  /**
   * Handle dropdown changes
   */
  const handleDropdownChange = (field, selected) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: selected };
      
      // Reset dependent fields when parent changes
      if (field === 'branch') {
        newData.usecase = null;
        newData.subusecase = null;
      } else if (field === 'usecase') {
        newData.subusecase = null;
      }
      
      // Update URL when all three are selected
      // COMMENTED OUT: Prevents auto-setting filter when selecting dropdowns in upload modal
      // if (field === 'subusecase' && selected && prev.branch && prev.usecase) {
      //   if (onPathChange) {
      //     onPathChange(prev.branch.value, prev.usecase.value, selected.value);
      //   }
      // } else if (field === 'usecase' && selected && prev.branch && newData.subusecase) {
      //   if (onPathChange) {
      //     onPathChange(prev.branch.value, selected.value, newData.subusecase.value);
      //   }
      // } else if (field === 'branch' && selected && newData.usecase && newData.subusecase) {
      //   if (onPathChange) {
      //     onPathChange(selected.value, newData.usecase.value, newData.subusecase.value);
      //   }
      // }
      
      return newData;
    });
    
    // Clear error for this field when user makes a selection
    if (touched) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Handle text input change
   */
  const handleTextChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, questionSetName: value }));
    
    // Clear error when user starts typing
    if (touched && value.trim()) {
      setErrors(prev => ({ ...prev, questionSetName: '' }));
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validate file type - Only accept PDF and Word documents
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      message.error('Please upload a valid file (.pdf, .docx)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      message.error('File size must be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    
    // Clear file error when user selects a valid file
    if (touched) {
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  /**
   * Handle drag events
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  /**
   * Handle drop event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  /**
   * Validate form fields
   */
  const validateForm = () => {
    const newErrors = {
      questionSetName: '',
      branch: '',
      usecase: '',
      subusecase: '',
      file: ''
    };
    
    let isValid = true;
    
    if (!formData.questionSetName.trim()) {
      newErrors.questionSetName = 'Question set name is required';
      isValid = false;
    }
    
    if (!formData.branch) {
      newErrors.branch = 'Branch is required';
      isValid = false;
    }
    
    if (!formData.usecase) {
      newErrors.usecase = 'Usecase is required';
      isValid = false;
    }
    
    if (!formData.subusecase) {
      newErrors.subusecase = 'Sub usecase is required';
      isValid = false;
    }
    
    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  /**
   * Handle modal close with reset
   */
  const handleClose = () => {
    setFormData({
      questionSetName: '',
      branch: null,
      usecase: null,
      subusecase: null
    });
    setSelectedFile(null);
    setDragActive(false);
    setErrors({
      questionSetName: '',
      branch: '',
      usecase: '',
      subusecase: '',
      file: ''
    });
    setTouched(false);
    onClose();
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setTouched(true);
    
    // Validate all fields
    if (!validateForm()) {
      message.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    const loadingMessage = message.loading('Uploading questions...', 0);

    try {
      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append('branch', formData.branch.value);
      formDataToSend.append('usecase', formData.usecase.value);
      formDataToSend.append('subusecase', formData.subusecase.value);
      formDataToSend.append('questionSetName', formData.questionSetName.trim());
      formDataToSend.append('file', selectedFile);

      const result = await QuestionService.uploadQuestions(formDataToSend);
      
      loadingMessage();
      
      if (result.success) {
        message.success(result.message || 'Questions uploaded successfully!');
        
        // Reset only the question set name, keep branch/usecase/subusecase for convenience
        setFormData({
          questionSetName: '',
          branch: formData.branch,
          usecase: formData.usecase,
          subusecase: formData.subusecase
        });
        setSelectedFile(null);
        
        onSuccess('processing'); // Pass 'processing' to redirect to Processing tab
      } else {
        message.error(result.message || 'Failed to upload questions');
      }
    } catch (error) {
      loadingMessage();
      console.error('Error uploading questions:', error);
      message.error(error.message || 'Failed to upload questions. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const modalStyle = {
    backgroundColor: isDark ? '#04002b' : '#ffffff',
    color: currentTheme.text.primary,
    borderRadius: '16px',
    maxWidth: '70rem',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: isDark 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: isDark ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
  };

  const dropzoneStyle = {
    border: `2px dashed ${dragActive ? '#FF6B47' : (isDark ? '#302d4c' : '#e9e8ea')}`,
    borderRadius: '12px',
    padding: '3rem 2rem',
    textAlign: 'center',
    backgroundColor: isDark ? '#1a1640' : '#f7f7f7',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#FF6B47',
      backgroundColor: isDark ? '#242143' : '#f0f0f0'
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={handleClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <Box 
            sx={{ 
              padding: '1.5rem',
              borderBottom: `1px solid ${isDark ? '#302d4c' : '#e9e8ea'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}
          >
            <Box>
              <Typography 
                sx={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 600, 
                  color: currentTheme.text.primary,
                  marginBottom: '0.25rem'
                }}
              >
                Upload Document
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '0.875rem', 
                  color: currentTheme.text.secondary
                }}
              >
                Upload a document based on desired branch, usecase and sub usecase
              </Typography>
            </Box>
            
            <IconButton 
              onClick={handleClose}
              sx={{ 
                color: currentTheme.text.primary,
                '&:hover': {
                  backgroundColor: isDark ? '#302d4c' : '#f0f0f0'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box 
            sx={{ 
              padding: '1.5rem',
              maxHeight: 'calc(90vh - 200px)',
              overflowY: 'auto'
            }}
          >
            {/* Question Set Name */}
            <Box sx={{ marginBottom: '1.5rem' }}>
              <Typography 
                sx={{ 
                  fontSize: '0.875rem', 
                  color: currentTheme.text.secondary,
                  marginBottom: '0.5rem'
                }}
              >
                Question set name <span style={{ color: '#e63e2b' }}>*</span>
              </Typography>
              <TextBox
                placeholder="Enter question set name"
                value={formData.questionSetName}
                onChange={handleTextChange}
                textBoxText={currentTheme.loginPage.textBoxText}
                textBoxBackground={isDark ? currentTheme.loginPage.textBoxBackground : undefined}
                themeMode={themeMode}
                disabled={uploading}
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  height: "2.5rem",
                  borderColor: errors.questionSetName ? '#e63e2b' : undefined
                }}
              />
              {errors.questionSetName && (
                <Typography 
                  sx={{ 
                    fontSize: '0.75rem', 
                    color: '#e63e2b',
                    marginTop: '0.25rem',
                    marginLeft: '0.5rem'
                  }}
                >
                  {errors.questionSetName}
                </Typography>
              )}
            </Box>

            {/* Branch, Usecase, Sub Usecase Dropdowns */}
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '1.5rem' }}>
              <Box sx={{ flex: 1 }}>
                <Dropdown
                  label="Branch *"
                  placeholder="Select Branch"
                  options={branchOptions}
                  value={formData.branch}
                  onChange={(selected) => handleDropdownChange('branch', selected)}
                  themeMode={themeMode}
                  disabled={uploading}
                  isSearchable={true}
                  width="100%"
                  error={!!errors.branch}
                  helperText={errors.branch}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Dropdown
                  label="Usecase *"
                  placeholder="Select Usecase"
                  options={usecaseOptions}
                  value={formData.usecase}
                  onChange={(selected) => handleDropdownChange('usecase', selected)}
                  themeMode={themeMode}
                  disabled={!formData.branch || uploading}
                  isSearchable={true}
                  width="100%"
                  error={!!errors.usecase}
                  helperText={errors.usecase}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Dropdown
                  label="Sub Usecase *"
                  placeholder="Select Sub Usecase"
                  options={subusecaseOptions}
                  value={formData.subusecase}
                  onChange={(selected) => handleDropdownChange('subusecase', selected)}
                  themeMode={themeMode}
                  disabled={!formData.usecase || uploading}
                  isSearchable={true}
                  width="100%"
                  error={!!errors.subusecase}
                  helperText={errors.subusecase}
                />
              </Box>
            </Box>

            {/* File Upload Dropzone */}
            <Box>
              <Box
                sx={{
                  ...dropzoneStyle,
                  borderColor: errors.file ? '#e63e2b' : (dragActive ? '#FF6B47' : (isDark ? '#302d4c' : '#e9e8ea'))
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
                
                <UploadFileIcon 
                  sx={{ 
                    fontSize: '3rem', 
                    color: errors.file ? '#e63e2b' : (dragActive ? '#FF6B47' : currentTheme.text.secondary),
                    marginBottom: '1rem'
                  }} 
                />
                
                {selectedFile ? (
                  <Typography sx={{ color: currentTheme.text.primary, fontWeight: 500 }}>
                    {selectedFile.name}
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ color: currentTheme.text.primary, fontWeight: 500, marginBottom: '0.5rem' }}>
                      Drag & drop (.pdf, .docx) <span style={{ color: '#e63e2b' }}>*</span>
                    </Typography>
                    <Typography sx={{ color: currentTheme.text.secondary, fontSize: '0.875rem' }}>
                      files to upload
                    </Typography>
                  </>
                )}
              </Box>
              {errors.file && (
                <Typography 
                  sx={{ 
                    fontSize: '0.75rem', 
                    color: '#e63e2b',
                    marginTop: '0.5rem',
                    marginLeft: '0.5rem'
                  }}
                >
                  {errors.file}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Footer */}
          <Box 
            sx={{ 
              padding: '1.5rem',
              borderTop: `1px solid ${isDark ? '#302d4c' : '#e9e8ea'}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <button
              onClick={handleClose}
              disabled={uploading}
              style={{
                padding: '0.625rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer',
                backgroundColor: isDark ? '#302d4c' : '#e9e8ea',
                color: currentTheme.text.primary,
                opacity: uploading ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={uploading}
              style={{
                padding: '0.625rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer',
                backgroundColor: '#FF6B47',
                color: '#ffffff',
                opacity: uploading ? 0.7 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {uploading && <CircularProgress size={16} sx={{ color: '#ffffff' }} />}
              {uploading ? 'Uploading...' : 'Submit'}
            </button>
          </Box>
        </div>
      </div>
    </>
  );
};

export default UploadQuestionModal;
